import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import en from '../i18n/en.json';
import fr from '../i18n/fr.json';
import es from '../i18n/es.json';
import de from '../i18n/de.json';
import it from '../i18n/it.json';
import pt from '../i18n/pt.json';
import nl from '../i18n/nl.json';
import pl from '../i18n/pl.json';
import tr from '../i18n/tr.json';
import ru from '../i18n/ru.json';
import ja from '../i18n/ja.json';
import zh from '../i18n/zh.json';
import ar from '../i18n/ar.json';
import hi from '../i18n/hi.json';

const translations = { en, fr, es, de, it, pt, nl, pl, tr, ru, ja, zh, ar, hi };
const SUPPORTED_LANGS = Object.keys(translations);
const I18nContext = createContext();

const countryToLang = {
  FR: 'fr', BE: 'fr', LU: 'fr', MC: 'fr', SN: 'fr', CI: 'fr', ML: 'fr', BF: 'fr', NE: 'fr', TG: 'fr', BJ: 'fr', GA: 'fr', CG: 'fr', CD: 'fr', CM: 'fr', MG: 'fr', HT: 'fr',
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es', EC: 'es', BO: 'es', PY: 'es', UY: 'es', CR: 'es', PA: 'es', DO: 'es', GT: 'es', HN: 'es', SV: 'es', NI: 'es', CU: 'es',
  DE: 'de', AT: 'de', CH: 'de', LI: 'de',
  IT: 'it', SM: 'it',
  PT: 'pt', BR: 'pt', AO: 'pt', MZ: 'pt',
  NL: 'nl',
  PL: 'pl',
  TR: 'tr',
  RU: 'ru', BY: 'ru', KZ: 'ru', KG: 'ru',
  JP: 'ja',
  CN: 'zh', TW: 'zh', HK: 'zh', MO: 'zh',
  SA: 'ar', AE: 'ar', EG: 'ar', MA: 'ar', DZ: 'ar', TN: 'ar', IQ: 'ar', JO: 'ar', LB: 'ar', KW: 'ar', QA: 'ar', BH: 'ar', OM: 'ar', YE: 'ar', LY: 'ar', SD: 'ar',
  IN: 'hi',
};

/** Read ?lang= from current URL */
function getLangFromURL() {
  const params = new URLSearchParams(window.location.search);
  const urlLang = params.get('lang');
  if (urlLang && translations[urlLang]) return urlLang;
  return null;
}

/** Update ?lang= in URL without navigation */
function setLangInURL(lang) {
  const url = new URL(window.location.href);
  url.searchParams.set('lang', lang);
  window.history.replaceState(null, '', url.toString());
}

function getInitialLang() {
  // Priority: URL param > localStorage > browser
  const fromURL = getLangFromURL();
  if (fromURL) return fromURL;
  const saved = localStorage.getItem('hexopdf_lang');
  if (saved && translations[saved]) return saved;
  const browser = navigator.language?.slice(0, 2);
  return translations[browser] ? browser : 'en';
}

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(getInitialLang);

  // Sync URL and localStorage on initial load
  useEffect(() => {
    setLangInURL(lang);
    localStorage.setItem('hexopdf_lang', lang);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-detect language from IP on first visit (no saved preference, no URL param)
  useEffect(() => {
    const saved = localStorage.getItem('hexopdf_lang');
    const fromURL = getLangFromURL();
    if (saved || fromURL) return;
    const ctrl = new AbortController();
    fetch('https://api.country.is/', { signal: ctrl.signal })
      .then(r => r.json())
      .then(data => {
        const detected = countryToLang[data.country];
        if (detected && translations[detected]) {
          setLang(detected);
          localStorage.setItem('hexopdf_lang', detected);
          setLangInURL(detected);
        }
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  // Listen for back/forward navigation (popstate) to sync lang from URL
  useEffect(() => {
    const onPop = () => {
      const urlLang = getLangFromURL();
      if (urlLang && urlLang !== lang) {
        setLang(urlLang);
        localStorage.setItem('hexopdf_lang', urlLang);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [lang]);

  const t = useCallback((path) => {
    const keys = path.split('.');
    let val = translations[lang];
    for (const k of keys) {
      val = val?.[k];
    }
    return val || path;
  }, [lang]);

  const switchLang = useCallback((newLang) => {
    if (translations[newLang]) {
      setLang(newLang);
      localStorage.setItem('hexopdf_lang', newLang);
      setLangInURL(newLang);
    }
  }, []);

  return (
    <I18nContext.Provider value={{ lang, t, switchLang, languages: SUPPORTED_LANGS }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
export { SUPPORTED_LANGS };
